import { useState, useCallback, useRef } from 'react';
import { useGameStore, BOARD_ROWS, BOARD_COLS } from '../store/useGameStore';
import { Cell } from './Cell';
import { LevelSelector } from './LevelSelector';
import { getShapeCells, getShapeAnchorOffset } from '../data/shapes';
import { COLOR_CSS, ELEMENT_CSS } from '../data/attributes';
import type { Stone } from '../types';
import './SkillPlate.css';

/** 计算拖拽时形状实际占用的棋盘格子列表 */
function computeDragCells(stone: Stone, hoverRow: number, hoverCol: number): { row: number; col: number }[] {
    const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
    const cells = getShapeCells(stone.shape, stone.currentRotation);
    return cells.map(([dr, dc]) => ({
        row: hoverRow - offset.dr + dr,
        col: hoverCol - offset.dc + dc,
    }));
}

/** 检查一组格子是否全部有效（在棋盘内、已激活、未被占用）。
 *  excludeStoneId：拖拽中的石头 id —— 它自己原本占用的格子视为空，
 *  这样旧位置 DOM 不变（拖拽不中断）但被拖石头"虚拟不占用"，
 *  能拖到与原位部分重叠的新位置。 */
function checkCellsValid(board: CellData[][], cells: { row: number; col: number }[], excludeStoneId?: string): boolean {
    for (const { row, col } of cells) {
        if (row < 0 || row >= BOARD_ROWS || col < 0 || col >= BOARD_COLS) return false;
        if (!board[row][col].active) return false;
        // 被拖拽石头自己占用的格子视为空，可重叠放置
        if (board[row][col].stoneId === excludeStoneId) continue;
        if (board[row][col].occupied) return false;
    }
    return true;
}

export function SkillPlate() {
    const {
        board, level, stones, placedStones,
        placeStone, removePlacedStone, selectStone, selectedStoneId,
        setDraggingStone, canPlaceStone, resetBoard, autoFillBoard, rotateStone,
    } = useGameStore();

    // 拖拽预览：形状实际占用的格子列表
    const [dragPreviewCells, setDragPreviewCells] = useState<{ row: number; col: number }[] | null>(null);
    const [dragPreviewValid, setDragPreviewValid] = useState(false);
    const [dragPreviewStone, setDragPreviewStone] = useState<Stone | null>(null);
    const [autoFillMsg, setAutoFillMsg] = useState<string | null>(null);
    const plateRef = useRef<HTMLDivElement>(null);
    const dragHoverRef = useRef<{ row: number; col: number } | null>(null);
    // 用 ref 暂存"待提交"的 hover 位置，配合 rAF 节流，把高频 dragover 合并成一次渲染
    const pendingHoverRef = useRef<{ row: number; col: number } | null>(null);
    const rafIdRef = useRef<number | null>(null);

    // 获取当前拖拽的石头信息
    const dragStoneId = useGameStore(s => s.draggingStoneId);

    const handleDragOver = useCallback((e: React.DragEvent, row: number, col: number) => {
        e.preventDefault();
        const currentDragId = useGameStore.getState().draggingStoneId;
        if (!currentDragId) return;

        // 同个格子不重复处理，避免 dragover 高频事件卡死页面
        const prev = dragHoverRef.current;
        if (prev && prev.row === row && prev.col === col) return;

        // 记下最新 hover 位置，延迟到下一帧统一更新状态
        pendingHoverRef.current = { row, col };
        if (rafIdRef.current !== null) return;
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            const hover = pendingHoverRef.current;
            const id = useGameStore.getState().draggingStoneId;
            if (!hover || !id) return;

            const state = useGameStore.getState();
            const stone = state.stones.find(s => s.id === id);
            if (!stone) return;

            const cells = computeDragCells(stone, hover.row, hover.col);
            dragHoverRef.current = hover;
            setDragPreviewCells(cells);
            setDragPreviewStone(stone);
            const valid = checkCellsValid(state.board, cells, id);
            setDragPreviewValid(valid);
        });
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragPreviewCells(null);
        setDragPreviewStone(null);
        setDragPreviewValid(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, row: number, col: number) => {
        e.preventDefault();
        // 取消任何挂起的 rAF，drop 时刻以当前 hover 格子为准
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        // 优先用 dragHoverRef（最新已提交的 hover 位置）现场重算，
        // 避免 rAF 异步导致闭包里 dragPreviewCells 过时
        const hover = dragHoverRef.current ?? pendingHoverRef.current;
        const id = dragStoneId ?? useGameStore.getState().draggingStoneId;
        if (!hover || !id) {
            setDraggingStone(null);
            setDragPreviewCells(null);
            setDragPreviewStone(null);
            return;
        }

        // 读取当前 store 状态，重新验证格子有效性
        const currentState = useGameStore.getState();
        const stone = currentState.stones.find(s => s.id === id);
        if (!stone) return;

        const cells = computeDragCells(stone, hover.row, hover.col);

        // 用当前棋盘重新验证（排除自身占用的格子）
        if (!checkCellsValid(currentState.board, cells, id)) {
            setDraggingStone(null);
            setDragPreviewCells(null);
            setDragPreviewStone(null);
            return;
        }

        // 如果石头已放置（从棋盘拖拽），先移除旧位置
        if (placedStones[id]) {
            removePlacedStone(id);
        }

        // 重新读取 store（removePlacedStone 后状态已更新），用预览格子放置
        const state = useGameStore.getState();
        const newBoard = state.board.map(row => row.map(cell => ({ ...cell })));
        for (const { row: r, col: c } of cells) {
            newBoard[r][c] = { ...newBoard[r][c], occupied: true, stoneId: id };
        }
        // 存矩阵原点（bounding box 左上角），渲染层用 row - offset.dr + dr 推算每个格子
        const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
        const firstCell = cells[0];
        const shapeCells = getShapeCells(stone.shape, stone.currentRotation);
        const firstMatrixCell = shapeCells.length > 0 ? shapeCells[0] : [0, 0];
        const matrixOrigin = {
            row: firstCell.row - firstMatrixCell[0] + offset.dr,
            col: firstCell.col - firstMatrixCell[1] + offset.dc,
        };
        useGameStore.setState({
            board: newBoard,
            placedStones: { ...state.placedStones, [id]: { row: matrixOrigin.row, col: matrixOrigin.col } },
        });
        state.recalcAttributes();

        // 清理拖拽状态
        dragHoverRef.current = null;
        pendingHoverRef.current = null;
        setDragPreviewCells(null);
        setDragPreviewStone(null);
        setDragPreviewValid(false);
        setDraggingStone(null);
    }, [dragStoneId, placedStones, removePlacedStone, setDraggingStone]);

    const handleCellClick = useCallback((row: number, col: number) => {
        // 点击已放置的石头，选中它
        const cell = board[row][col];
        if (cell.stoneId) {
            selectStone(cell.stoneId);
        } else {
            selectStone(null);
        }
    }, [board, selectStone]);

    const handleStoneDragStart = useCallback((e: React.DragEvent, stoneId: string) => {
        selectStone(stoneId);
        // 不 removePlacedStone，石头保留在棋盘上（DOM不消失，拖拽不中断）
        // 有效性检查由 handleDragOver 拷贝棋盘时清除自身格子来处理
        setDraggingStone(stoneId);
        e.dataTransfer.setData('text/plain', stoneId);
        e.dataTransfer.effectAllowed = 'move';
    }, [selectStone, setDraggingStone]);

    const handleRemoveSelected = useCallback(() => {
        if (selectedStoneId) {
            removePlacedStone(selectedStoneId);
            selectStone(null);
        }
    }, [selectedStoneId, removePlacedStone, selectStone]);

    const handleAutoFill = useCallback(() => {
        const result = autoFillBoard();
        if (result) {
            setAutoFillMsg('✓ 自动铺满成功！');
        } else {
            setAutoFillMsg('✗ 无法用当前石头铺满机巧盘，请调整石头数量或形状');
        }
        setTimeout(() => setAutoFillMsg(null), 3000);
    }, [autoFillBoard]);

    // 获取已放置的石头，用于渲染覆盖层
    const placedStoneEntries = Object.entries(placedStones).map(([stoneId, pos]) => {
        const stone = stones.find(s => s.id === stoneId);
        if (!stone) return null;
        return { stone, ...pos };
    }).filter(Boolean) as { stone: Stone; row: number; col: number }[];

    return (
        <div className="skill-plate-wrapper">
            <div className="plate-header">
                <LevelSelector />
                <div className="plate-actions">
                    <button className="btn btn-auto" onClick={handleAutoFill} title="自动计算最优铺满方案">
                        ⚡ 自动铺满
                    </button>
                    <button className="btn btn-reset" onClick={resetBoard} title="清空所有放置">
                        ⟳ 重置
                    </button>
                    {selectedStoneId && (
                        <>
                            <button className="btn btn-rotate" onClick={() => rotateStone(selectedStoneId)} title="旋转选中的石头">
                                ⟳ 旋转
                            </button>
                            <button className="btn btn-remove" onClick={handleRemoveSelected} title="移除选中的石头">
                                ✕ 移除选中
                            </button>
                        </>
                    )}
                </div>
            </div>

            {autoFillMsg && (
                <div className="auto-fill-toast">{autoFillMsg}</div>
            )}

            <div className="board-container">
                <div className="board" ref={plateRef} style={{ '--rows': BOARD_ROWS, '--cols': BOARD_COLS } as React.CSSProperties}>
                    {/* 网格层 */}
                    <div className="grid-layer">
                        {board.map((row, r) =>
                            row.map((cell, c) => (
                                <Cell
                                    key={`${r}-${c}`}
                                    row={r} col={c}
                                    active={cell.active}
                                    occupied={cell.occupied && !selectedStoneId}
                                    stoneId={cell.stoneId}
                                    isHovering={false}
                                    canPlace={false}
                                    onDragOver={(e) => handleDragOver(e, r, c)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, r, c)}
                                    onCellClick={() => handleCellClick(r, c)}
                                />
                            ))
                        )}
                    </div>

                    {/* 拖拽预览层：拖拽时显示完整形状 */}
                    {dragPreviewCells && dragPreviewStone && (
                        <div className="overlay-layer">
                            {dragPreviewCells.map(({ row, col }) => (
                                <div
                                    key={`drag-${row}-${col}`}
                                    className={`stone-cell-overlay drag-preview ${dragPreviewValid ? 'drag-valid' : 'drag-invalid'}`}
                                    style={{
                                        '--cell-row': row + 1,
                                        '--cell-col': col + 1,
                                        background: dragPreviewValid
                                            ? `linear-gradient(135deg, ${COLOR_CSS[dragPreviewStone.color]}66, ${COLOR_CSS[dragPreviewStone.color]}33)`
                                            : 'linear-gradient(135deg, rgba(239,68,68,0.4), rgba(239,68,68,0.2))',
                                        border: `2px solid ${dragPreviewValid ? COLOR_CSS[dragPreviewStone.color] : 'rgba(239,68,68,0.7)'}`,
                                        boxShadow: dragPreviewValid
                                            ? `0 0 8px ${COLOR_CSS[dragPreviewStone.color]}66, inset 0 0 4px ${COLOR_CSS[dragPreviewStone.color]}33`
                                            : '0 0 8px rgba(239,68,68,0.5), inset 0 0 4px rgba(239,68,68,0.3)',
                                        borderRadius: '3px',
                                        pointerEvents: 'none',
                                        zIndex: 5,
                                    } as React.CSSProperties}
                                />
                            ))}
                        </div>
                    )}

                    {/* 已放置石头的覆盖层 */}
                    <div className="overlay-layer">
                        {placedStoneEntries.map(({ stone, row, col }) => {
                            const cells = getShapeCells(stone.shape, stone.currentRotation);
                            // placedStones 存的已是"形状第一个实际格子"坐标，渲染时减去 offset 还原矩阵原点
                            const offset = getShapeAnchorOffset(stone.shape, stone.currentRotation);
                            const baseRow = row - offset.dr;
                            const baseCol = col - offset.dc;
                            const color = COLOR_CSS[stone.color];
                            const isSelected = selectedStoneId === stone.id;

                            return cells.map(([dr, dc]) => {
                                const cellRow = baseRow + dr;
                                const cellCol = baseCol + dc;
                                const attrText = stone.attributes.map(a => `${a.label}+${a.value}`).join('  ');
                                return (
                                    <div
                                        key={`${stone.id}-${cellRow}-${cellCol}`}
                                        className={`stone-cell-overlay ${isSelected ? 'selected' : ''}`}
                                        title={`${stone.type === 'craft' ? '匠心石' : '机巧石'} (${stone.color === 'white' ? '白' : stone.color === 'green' ? '绿' : stone.color === 'blue' ? '蓝' : '紫'}) | ${attrText}`}
                                        draggable
                                        onDragStart={(e) => handleStoneDragStart(e, stone.id)}
                                        style={{
                                            '--cell-row': cellRow + 1,
                                            '--cell-col': cellCol + 1,
                                            background: `linear-gradient(135deg, ${color}44, ${color}22)`,
                                            border: `1px solid ${isSelected ? '#fff' : color}`,
                                            boxShadow: isSelected
                                                ? `0 0 10px ${color}99, inset 0 0 6px ${color}44`
                                                : `0 0 4px ${color}44, inset 0 0 3px ${color}22`,
                                            borderRadius: '3px',
                                        } as React.CSSProperties}
                                        onClick={() => selectStone(stone.id)}
                                    >
                                        <div className="stone-cell-attr-label">
                                            {stone.attributes.map((a, i) => (
                                                <span key={a.id} className="attr-short" style={{ color }}>
                          {a.label}
                        </span>
                                            ))}
                                        </div>
                                        {stone.attributes.some(a => a.element) && (
                                            <div className="stone-cell-element"
                                                 style={{
                                                     background: `radial-gradient(circle, ${ELEMENT_CSS[stone.attributes.find(a => a.element)?.element || 'fire']}44, transparent)`,
                                                 }}
                                            />
                                        )}
                                    </div>
                                );
                            });
                        })}
                    </div>
                </div>

                <div className="board-info">
                    <span>等级 {level} | </span>
                    <span>
            已放置: {Object.keys(placedStones).length} 块石头 |
            占用: {Object.keys(placedStones).length * 4} / {board.flat().filter(c => c.active).length} 格
          </span>
                </div>
            </div>
        </div>
    );
}